import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  Info,
  ShieldCheck,
  XCircle,
} from "@phosphor-icons/react";
import { fetchProductBillingStatus } from "@/lib/admin/billingActions";

const NOT_CONFIGURED = "not_configured";

/**
 * Admin · /admin/billing/pricing
 *
 * Organization-level HoneyAcorn billing is intentionally separate from the
 * operational family invoice tools at /admin/billing. This page remains a
 * safe placeholder until pricing and a plan catalog are explicitly approved.
 */
export default function ProductBilling() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchProductBillingStatus()
      .then((data) => {
        if (alive) setStatus(data);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const isNotConfigured = status?.status === NOT_CONFIGURED;

  return (
    <div data-testid="admin-product-billing" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/admin/billing"
            data-testid="product-billing-back"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-hover hover:underline"
          >
            <ArrowLeft size={12} weight="bold" />
            Back to operational billing
          </Link>
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Product billing
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Pricing &amp; plan
          </h1>
          <p className="mt-2 max-w-2xl text-neutral-600">
            Organization-level HoneyAcorn billing will live here when pricing
            is approved. Family invoices and tuition operations remain in
            operational billing.
          </p>
        </div>
        <span
          data-testid="product-billing-status"
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-amber-900"
        >
          <Clock size={13} weight="bold" />
          {status?.status || "Loading"}
        </span>
      </header>

      {error && (
        <section
          data-testid="product-billing-error"
          role="alert"
          className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-950"
        >
          <h2 className="font-heading text-lg font-semibold">Billing status unavailable</h2>
          <p className="mt-1 leading-relaxed">
            Pricing is not available from this screen. No payment action has
            been enabled.
          </p>
        </section>
      )}

      {!error && (
        <>
          <section
            data-testid="product-billing-placeholder"
            className="rounded-3xl border border-primary/25 bg-gradient-to-br from-primary/10 via-white to-secondary/20 p-6 shadow-sm sm:p-8"
          >
            <div className="flex max-w-3xl items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-primary-hover shadow-sm">
                <ShieldCheck size={24} weight="duotone" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-hover">
                  Billing is not configured
                </p>
                <h2 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
                  Pricing is coming soon. We&rsquo;re finalizing the right plan for your organization.
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-700">
                  There is no plan to choose, subscription to manage, or
                  payment to collect here yet. Your access to HoneyAcorn is
                  not blocked by this placeholder.
                </p>
              </div>
            </div>
          </section>

          <section
            data-testid="product-billing-controls"
            className="rounded-2xl border border-neutral-200 bg-white p-5"
          >
            <div className="flex items-start gap-3">
              <Info size={19} weight="duotone" className="mt-0.5 text-primary-hover" />
              <div>
                <h2 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
                  Nothing to manage yet
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-neutral-600">
                  The billing contract is ready to accept approved plan
                  configuration later. Until then, all billing actions on
                  this page stay disabled.
                </p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <ReadinessItem
                label="Plan"
                value={isNotConfigured ? "Not configured" : "Unavailable"}
                enabled={!isNotConfigured}
              />
              <ReadinessItem
                label="Entitlements"
                value={isNotConfigured ? "Not configured" : "Unavailable"}
                enabled={!isNotConfigured}
              />
              <ReadinessItem
                label="Payment actions"
                value="Disabled"
                enabled={false}
              />
            </div>
          </section>

          <p
            data-testid="product-billing-no-cta"
            className="text-center text-xs leading-relaxed text-neutral-500"
          >
            No pricing contact route is configured in the product yet.
          </p>
        </>
      )}
    </div>
  );
}

function ReadinessItem({ label, value, enabled }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
      <div className="flex items-center gap-2">
        {enabled ? (
          <CheckCircle size={16} weight="fill" className="text-primary" />
        ) : (
          <XCircle size={16} weight="fill" className="text-neutral-400" />
        )}
        <p className="text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
          {label}
        </p>
      </div>
      <p className="mt-2 text-sm font-semibold text-neutral-900">{value}</p>
    </div>
  );
}
